import axios from 'axios';

import React, { useState, useEffect, useRef, useCallback, useContext} from 'react';
import { AuthContext } from './App';
import { useNavigate } from 'react-router-dom';

import { useTransition, animated, config } from 'react-spring';
import {
    OfficeBuildingIcon,
    MailIcon,
    TrashIcon,
    LocationMarkerIcon,
    BriefcaseIcon,
    ChevronDownIcon,
    AcademicCapIcon, 
    UserCircleIcon
  } from '@heroicons/react/solid';
import TextEditor from './TextEditor';
import debounce from 'lodash.debounce';

function Form() {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const currentMonth = new Date().getMonth(); // this gives you the month index (0 for January, 11 for December)
    const currentYear = new Date().getFullYear(); // this gives you the current year
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const [jobOfferData, setJobOfferData] = useState({
        email: '',
        experience: '',
        degree: '',
        rates: {},
        offers: [{
          id: Date.now(),
          status: 'Choose Status',
          company: '',
          title: '',
          customTitle: '',
          jobFamily: '',
          focus: '',
          otherFocus: '',
          level: '',
          otherLevel: '',
          location: '',
          workMode: '',
          baseSalary: 0,
          baseSalaryCurrency: 'USD', 
          stock: 0,
          stockType: '',
          stockCurrency: 'USD', 
          signingBonus: 0,
          signingBonusCurrency: 'USD', 
          targetBonus: 0,
          targetBonusCurrency: 'USD', 
          totalCompensation: 0,
          month: months[currentMonth],
          year: currentYear,
          offerDate: '',
          searchResults: [],
          locationSearchResults: [],
          titleSearchResults: [],
          isDropdownVisible: false,
          isLocationDropdownVisible: false,
          isTitleDropdownVisible: false,
          jobFamilyDropdown: false,
          focusDropdown: false,
          levelDropdown: false, 
          isStatusDropdownVisible: false,
          isWorkModeDropdownVisible: false,
          isMonthDropdownVisible: false,
          isYearDropdownVisible: false
        }]
      });
    const [rates, setRates] = useState({});
    const [touched, setTouched] = useState({
        email: false,
        experience: false,
        degree: false,
    });
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessages, setNotificationMessages] = useState([]);    
    const wrapperRef = useRef(null); // This ref should be attached to the dropdown container
    const locationWrapperRef = useRef(null);
    const autocompleteServiceRef = useRef(null);
    const [showDegreeDropdown, setShowDegreeDropdown] = useState(false);
    const degreeOptions = ["", "High School", "Associate", "Bachelors", "Masters", "Doctoral/PhD."];
    const jobFamilyOptions = ["", "Software Engineer", "Data Scientist", "Research Scientist", "Product Manager"]
    const focusOptions = ["", "iOS", "Android", "Mobile (iOS + Android)", "Web Development (Front-End)", "AI/ML", "Distributed Systems (Back-End)", "API Development (Back-End)", "Full Stack", "Networking", "Data", "Production", "Security", "DevOps", "Site Reliability (SRE)", "AR/VR", "Blockchain", "Game Development", "Quantum Computing", "Robotics", "Computational Biology", "Other"];
    const levelOptions = ["", "Intern", "Junior", "Mid-Level", "Senior", "Staff", "Senior Staff", "Distinguished", "Other"];

    const workModeOptions = ["Remote", "In-Person", "Hybrid"];
    const statusOptions = ["Denied", "Offered", "Accepted"];
    // const currentYear = new Date().getFullYear();
    const years = Array.from({length: 50}, (_, i) => currentYear - i);

    const [content, setContent] = useState(``);

    const handleEditorChange = (content) => {
        setContent(content);
    };

    useEffect(() => {
        if (auth.isLoggedIn) {
            setJobOfferData((prevState) => ({
                ...prevState,
                email: auth.email // Automatically set the email from auth
            }));
        }
    }, [auth.isLoggedIn, auth.email]); // Runs whenever auth state changes

    useEffect(() => {
        axios.get('https://api.finaloffers.fyi/api/rates')
            .then(response => {
                setRates(response.data);
            })
            .catch(error => console.error('Error fetching currency rates:', error));
    }, []);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setJobOfferData(prevData => {
                    const updatedOffers = prevData.offers.map(offer => {
                        let updatedOffer = {...offer};
    
                        // Handle company dropdown visibility
                        if (offer.isDropdownVisible && offer.searchResults.length > 0) {
                            updatedOffer = {
                                ...updatedOffer,
                                company: offer.searchResults[0].name, // or maintain the current value if you don't want to auto-select
                                isDropdownVisible: false
                            };
                        } else {
                            updatedOffer = {...updatedOffer, isDropdownVisible: false};
                        }
    
                        return updatedOffer;
                    });
    
                    return {...prevData, offers: updatedOffers};
                });
            }

            if (locationWrapperRef.current && !locationWrapperRef.current.contains(event.target)) {
                setJobOfferData(prevData => {
                    const updatedOffers = prevData.offers.map(offer => {
                        let updatedOffer = {...offer};
    
                        // Handle location dropdown visibility
                        if (offer.isLocationDropdownVisible && offer.locationSearchResults.length > 0) {
                            updatedOffer = {
                                ...updatedOffer,
                                location: offer.locationSearchResults[0], // or maintain the current value if you don't want to auto-select
                                isLocationDropdownVisible: false
                            };
                        } else {
                            updatedOffer = {...updatedOffer, isLocationDropdownVisible: false};
                        }

                        // if (offer.isWorkModeDropdownVisible) {
                        //     updatedOffer = {...updatedOffer, isWorkModeDropdownVisible: false};
                        // }

                        // if (offer.isMonthDropdownVisible) {
                        //     updatedOffer = {...updatedOffer, isMonthDropdownVisible: false};
                        // }

                        // if (offer.isYearDropdownVisible) {
                        //     updatedOffer = {...updatedOffer, isYearDropdownVisible: false};
                        // }
    
                        return updatedOffer;
                    });
    
                    return {...prevData, offers: updatedOffers};
                });
            }
        }
    
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    

    useEffect(() => {
        // Load Google Places script
        const loadGoogleMapsScript = () => {
            if (!window.google) {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDtxdG6etewvkpTp6W79Z9PLg-9QTDX85w&libraries=places`;
                document.head.append(script);
                script.onload = () => {
                    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
                };
                return () => script.remove();
            } else {
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            }
        };

        loadGoogleMapsScript();
    }, []);

    // const [isExperienceValid, setIsExperienceValid] = useState(true);
    const [isExperienceValid, setIsExperienceValid] = useState(true);
    const [isDegreeSelected, setIsDegreeSelected] = useState(true);
    const [suggestions, setSuggestions] = useState([]);
    const [input, setInput] = useState('');

    const cleanOffer = (offer) => {
        const {
            searchResults, locationSearchResults, titleSearchResults,
            isDropdownVisible, isLocationDropdownVisible, isTitleDropdownVisible,
            ...cleanedOffer
        } = offer;
        return cleanedOffer;
    };

    const validateEmail = (email) => {
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email.toLowerCase());
    };
    
    const validateExperience = (experience) => {
        return experience.toString().length > 0 && experience >= 0 && experience <= 50;
    };
    
    const handleBlur = (field) => {
        setTouched({ ...touched, [field]: true });
    
        if (field === 'email') {
          const isValidEmail = validateEmail(jobOfferData.email);
          // Optionally set some state based on validation
        } else if (field === 'experience') {
          const isValidExperience = validateExperience(jobOfferData.experience);
          setIsExperienceValid(isValidExperience);
        } else if (field === 'degree') {
          const isDegreeChosen = jobOfferData.degree !== '';
          setIsDegreeSelected(isDegreeChosen);
        }
    };

    const handleDetailChange = (index, event) => {
        const updatedOffers = jobOfferData.offers.map((offer, offerIndex) => {
            if (index === offerIndex) {
                return {
                    ...offer,
                    [event.target.name]: event.target.value,
                    isJobStatusDropdownVisible: false
                };
            }
            return offer;
        });

        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    // const handleStatusChange = (index, event) => {
    //     const updatedOffers = jobOfferData.offers.map((offer, offerIndex) => {
    //         if (index === offerIndex) {
    //             return {
    //                 ...offer,
    //                 status: event.target.value
    //             };
    //         }
    //         return offer;
    //     });

    //     setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    //     // setIsDropdownOpen(prevState => ({ ...prevState, [index]: false }));
    // };

    const handleStatusChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    status: value,
                    isStatusDropdownVisible: !offer.isStatusDropdownVisible // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    // const handleWorkModeChange = (index, event) => {
    //     const updatedOffers = jobOfferData.offers.map((offer, offerIndex) => {
    //         if (index === offerIndex) {
    //             return {
    //                 ...offer,
    //                 workMode: event.target.value
    //             };
    //         }
    //         return offer;
    //     });

    //     setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    // };
    const handleWorkModeChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    workMode: value,
                    isWorkModeDropdownVisible: !offer.isWorkModeDropdownVisible // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const handleJobFamilyChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    jobFamily: value,
                    jobFamilyDropdown: !offer.jobFamilyDropdown // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const handleFocusChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    focus: value,
                    otherFocus: '', 
                    focusDropdown: !offer.focusDropdown // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const handleLevelChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    level: value,
                    otherFocus: '', 
                    levelDropdown: !offer.levelDropdown // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const handleOfferDateMonthChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    month: value,
                    isMonthDropdownVisible: false // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const handleOfferDateYearChange = (offerIndex, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return {
                    ...offer,
                    year: value,
                    isYearDropdownVisible: false // Close dropdown after selection
                };
            }
            return offer;
        });
    
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleStatusDropdown = (offerIndex) => {
        // const updatedOffers = jobOfferData.offers.map(offer => {
        //     if (offer.id === offerId) {
        //         return { ...offer, title: value };
        //     }
        //     return offer;
        // });
        // setJobOfferData({ ...jobOfferData, offers: updatedOffers });
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return { ...offer, isStatusDropdownVisible: !offer.isStatusDropdownVisible };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleWorkModeDropdown = (offerIndex) => {
        // const updatedOffers = jobOfferData.offers.map(offer => {
        //     if (offer.id === offerId) {
        //         return { ...offer, title: value };
        //     }
        //     return offer;
        // });
        // setJobOfferData({ ...jobOfferData, offers: updatedOffers });
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return { ...offer, isWorkModeDropdownVisible: !offer.isWorkModeDropdownVisible };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleJobFamilyDropdown = (offerIndex) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return { ...offer, jobFamilyDropdown: !offer.jobFamilyDropdown };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleFocusDropdown = (offerIndex) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return { ...offer, focusDropdown: !offer.focusDropdown };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleLevelDropdown = (offerIndex) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerIndex) {
                return { ...offer, levelDropdown: !offer.levelDropdown };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleMonthDropdown = (index) => {
        const updatedOffers = jobOfferData.offers.map((offer, offerIndex) => {
            if (index === offerIndex) {
                return { ...offer, isMonthDropdownVisible: !offer.isMonthDropdownVisible };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    const toggleYearDropdown = (index) => {
        const updatedOffers = jobOfferData.offers.map((offer, offerIndex) => {
            if (index === offerIndex) {
                return { ...offer, isYearDropdownVisible: !offer.isYearDropdownVisible };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    };

    // const handleDateChange = (index, value, field) => {
    //     const updatedOffers = jobOfferData.offers.map((offer, offerIndex) => {
    //         if (index === offerIndex) {
    //             if(field === "Month"){
    //                 return { ...offer, isWorkModeDropdownVisible: !offer.isWorkModeDropdownVisible };
    //             }
                
    //             if(field === "Year"){
    //                 return { ...offer, offerDate: new Date(selectedYear, selectedMonth, 1) };
    //             }
    //         }
    //         return offer;
    //     });
    //     setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    // };
 
    const debouncedHandleCompanySearch = useCallback(
        debounce((offerId, value) => {
          if (value.length > 2) {
            fetch(`https://autocomplete.clearbit.com/v1/companies/suggest?query=${value}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer your_api_key` }
            })
            .then(response => response.json())
            .then(data => {
                const results = data.map(company => ({
                    name: company.name,
                    domain: company.domain
                }));
                setJobOfferData(prevData => ({
                    ...prevData,
                    offers: prevData.offers.map(offer => {
                        if (offer.id === offerId) {
                            return { ...offer, searchResults: results, isDropdownVisible: true };
                        }
                        return offer;
                    })
                }));
            })
            .catch(error => console.error('Error fetching companies:', error));
          } else {
            setJobOfferData(prevData => ({
                ...prevData,
                offers: prevData.offers.map(offer => {
                    if (offer.id === offerId) {
                        return { ...offer, searchResults: [], isDropdownVisible: false };
                    }
                    return offer;
                })
            }));
          }
        }, 100),
        [] // Empty dependency array to ensure the function is not recreated on every render
      );


    const handleCompanySearchChange = (offerId, value) => {
        setJobOfferData(prevData => ({
            ...prevData,
            offers: prevData.offers.map(offer => {
                if (offer.id === offerId) {
                    return { ...offer, company: value };
                }
                return offer;
            })
        }));
        
        debouncedHandleCompanySearch(offerId, value);
    };

    const debouncedLocationSearch = useCallback(
        debounce((offerId, value) => {
            if (!value.length || value.length <= 2) {
                // Clear results if the input is too short
                setJobOfferData(prevData => ({
                    ...prevData,
                    offers: prevData.offers.map(offer => {
                        if (offer.id === offerId) {
                            return { ...offer, locationSearchResults: [], isLocationDropdownVisible: false };
                        }
                        return offer;
                    })
                }));
                return;
            }
    
            if (autocompleteServiceRef.current) {
                autocompleteServiceRef.current.getPlacePredictions({
                    input: value,
                    types: ['(cities)']
                }, (predictions, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        const results = predictions.map(pred => pred.description);
                        setJobOfferData(prevData => ({
                            ...prevData,
                            offers: prevData.offers.map(offer => {
                                if (offer.id === offerId) {
                                    return {
                                        ...offer,
                                        locationSearchResults: results,
                                        isLocationDropdownVisible: true
                                    };
                                }
                                return offer;
                            })
                        }));
                    } else {
                        console.error('Google Places API error:', status);
                    }
                });
            }
        }, 100),
        []  // No dependencies; the function does not need to be recreated unless the component unmounts
    );

    const handleLocationSearchChange = (offerId, value) => {
        // Immediately update the location value
        setJobOfferData(prevData => ({
            ...prevData,
            offers: prevData.offers.map(offer => {
                if (offer.id === offerId) {
                    return { ...offer, location: value };
                }
                return offer;
            })
        }));
    
        // Call the debounced search
        debouncedLocationSearch(offerId, value);
    };    

    const handleTitleSearchChange = (offerId, value) => {
        console.log("Component");
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerId) {
                return { ...offer, title: value };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    
        if (value.length > 2) {
            axios.get(`https://api.finaloffers.fyi/api/titles?term=${value}`)
                .then(response => {
                    const updatedOffersWithResults = updatedOffers.map(offer => {
                        if (offer.id === offerId) {
                            return { ...offer, titleSearchResults: response.data, isTitleDropdownVisible: true };
                        }
                        return offer;
                    });
                    setJobOfferData({ ...jobOfferData, offers: updatedOffersWithResults });
                })
                .catch(error => {
                    console.error('Error fetching locations:', error);
                });
        } else {
            const updatedOffersWithResultsCleared = updatedOffers.map(offer => {
                if (offer.id === offerId) {
                    return { ...offer, titleSearchResults: [], isTitleDropdownVisible: false };
                }
                return offer;
            });
            setJobOfferData({ ...jobOfferData, offers: updatedOffersWithResultsCleared });
        }
    };

    const handleCustomTitleChange = (offerId, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerId) {
                return { ...offer, customTitle: value };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    
        
    };

    const handleOtherFocusChange = (offerId, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerId) {
                return { ...offer, otherFocus: value };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    
        
    };

    const handleOtherLevelChange = (offerId, value) => {
        const updatedOffers = jobOfferData.offers.map(offer => {
            if (offer.id === offerId) {
                return { ...offer, otherFocus: value };
            }
            return offer;
        });
        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
    
        
    };

    function validateFields(data) {
        const errors = [];
    
        if (data.email && !validateEmail(data.email)) {
            errors.push("Email doesn't look valid.");
        }
        if (data.experience === '' || !validateExperience(data.experience)) {
            errors.push("Please enter valid experience years (0-50).");
        }
        if (!data.degree) {
            errors.push("Please choose your last degree.");
        }
    
        // Add more validations for other fields as needed
        // Example: Check if a list of offers is empty
        if (data.offers.length === 0) {
            errors.push("Please add at least one job offer.");
        }

        // Validate each offer
        data.offers.forEach((offer, index) => {
            if(!offer.status){
                if (data.offers.length === 1){
                    errors.push(`Please select the status of your offer.`);
                }
                else{
                    errors.push(`Please select the status for offer ${index + 1}.`);
                }
            }

            if (!offer.company) {
                if (data.offers.length === 1){
                    errors.push(`Missing company name in offer.`);
                }
                else{
                    errors.push(`Missing company name in offer ${index + 1}.`);
                }
            }

            if (!offer.title) {
                if (data.offers.length === 1){
                    errors.push(`Missing title name in offer.`);
                }
                else{
                    errors.push(`Missing title name in offer ${index + 1}.`);
                }
            }

            if (offer.title === "Other" && !offer.customTitle){
                if (data.offers.length === 1){
                    errors.push(`Missing Custom Title in the offer.`)
                }
                else{
                    errors.push(`Missing Custom Title in offer ${index + 1}.`);
                }
                
            }

            // if (offer.title === "Other" && !offer.jobFamily){
            //     if (data.offers.length === 1){
            //         errors.push(`Please select the matching job family for your custom title in offer.`)
            //     }
            //     else{
            //         errors.push(`Please select the matching job family for your custom title in offer ${index + 1}.`);
            //     }
            // }

            

            if (offer.status === "Accepted" || offer.status === "Offered") {
                if (data.offers.length === 1){
                    if (!offer.focus){
                        errors.push(`Missing focus info in the offer.`);
                    }

                    if (!offer.level){
                        errors.push(`Missing level info in the offer.`);
                    }

                    if (!offer.location){
                        errors.push(`Missing location in offer.`);
                    }

                    if (!offer.workMode){
                        errors.push(`Missing work mode in the offer.`);
                    }
                    
                    if (offer.baseSalary === 0 && offer.stock === 0 && offer.signingBonus === 0 && offer.targetBonus === 0) {
                        errors.push(`Please enter at least one of the fields related to your compensation.`);
                    }

                    if (offer.stock !== 0 && offer.stockType === ""){
                        errors.push(`Please choose the type of your stock for the offer.`);
                    }

                    // if (!offer.month){
                    //     errors.push(`Please choose the month when the offer was made.`);
                    // }

                    // if (!offer.year){
                    //     errors.push(`Please choose the year when the offer was made.`);
                    // }
                }
                else{
                    if (!offer.focus){
                        errors.push(`Missing focus info in the offer ${index + 1}.`);
                    }

                    if (!offer.level){
                        errors.push(`Missing level info in the offer ${index + 1}.`);
                    }

                    if (!offer.location){
                        errors.push(`Missing location in the offer ${index + 1}.`);
                    }

                    if (!offer.workMode){
                        errors.push(`Missing work mode in the offer ${index + 1}.`);
                    }
                    
                    // if (offer.baseSalary === '' || isNaN(offer.baseSalary)) {
                    //     errors.push(`Invalid or missing base salary details in the offer ${index + 1}.`);
                    // }

                    // if (offer.stock && offer.stockType === ""){
                    //     errors.push(`Please choose the type of your stock for the offer ${index + 1}.`);
                    // }

                    if (offer.baseSalary === 0 && offer.stock === 0 && offer.signingBonus === 0 && offer.targetBonus === 0) {
                        errors.push(`Please fill at least one of the fields related to your compensation for offer ${index + 1}.`);
                    }

                    if (offer.stock !== 0 && offer.stockType === ""){
                        errors.push(`Please choose the type of your stock for the offer ${index + 1}.`);
                    }

                    // if (!offer.month){
                    //     errors.push(`Please choose the month when the offer ${index + 1} was made.`);
                    // }

                    // if (!offer.year){
                    //     errors.push(`Please choose the year when the offer ${index + 1} was made.`);
                    // }
                }
                
            }
            
        });
    
        return errors;
    }
    


    const submitForm = async (event) => {
        event.preventDefault();

        const errors = validateFields(jobOfferData);
        if (errors.length > 0) {
            setNotificationMessages(errors);
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 5000); // Hide notifications after 5 seconds
            return;
        }

        console.log("Submitting offers:", jobOfferData.offers);

        const preparedOffers = jobOfferData.offers.map(offer => {
            const cleanedOffer = cleanOffer(offer);
            // const { status, baseSalary, stock, signingBonus, targetBonus, joiningDate, ...rest } = cleanedOffer;
            const {
                status,
                baseSalary, baseSalaryCurrency,
                stock, stockCurrency,
                signingBonus, signingBonusCurrency,
                targetBonus, targetBonusCurrency,
                joiningDate,
                ...rest
            } = cleanedOffer;
    
            let finalOffer = { ...rest, status };
    
            // if (status === 'Accepted' || status === 'Offered') {
            //     finalOffer.salaryDetails = {
            //         baseSalary,
            //         stock,
            //         signingBonus,
            //         targetBonus
            //     };
            // }
            // if (status === 'Accepted' || status === 'Offered') {
            //     finalOffer.salaryDetails = {
            //         baseSalary: convertToUSD(offer.baseSalary, offer.currency),
            //         stock: convertToUSD(offer.stock, offer.currency),
            //         signingBonus: convertToUSD(offer.signingBonus, offer.currency),
            //         targetBonus: convertToUSD(offer.targetBonus, offer.currency),
            //     };
            // }
            if (status === 'Accepted' || status === 'Offered') {
                finalOffer.salaryDetails = {
                    baseSalary: convertToUSD(offer.baseSalary, offer.baseSalaryCurrency),
                    stock: convertToUSD(offer.stock, offer.stockCurrency),
                    signingBonus: convertToUSD(offer.signingBonus, offer.signingBonusCurrency),
                    targetBonus: convertToUSD(offer.targetBonus, offer.targetBonusCurrency),
                    totalCompensation: parseFloat(convertToUSD(offer.baseSalary, offer.baseSalaryCurrency) || 0) + parseFloat(convertToUSD(offer.stock, offer.stockCurrency) || 0) + parseFloat(convertToUSD(offer.targetBonus, offer.targetBonusCurrency) || 0)
                };

                finalOffer.offerDate = new Date(finalOffer.year, finalOffer.month, 1);
            }

            if (status === 'Denied') {
                finalOffer.salaryDetails = {
                    baseSalary: '',
                    stock: '',
                    signingBonus: '',
                    targetBonus: '',
                    totalCompensation: ''
                };

                finalOffer.offerDate = new Date(currentYear, months[currentMonth], 1);
            }
    
            return finalOffer;
        });
        
        // console.log("Prepared offers to send:", preparedOffers);
        console.log("Prepared offers to send (in USD):", preparedOffers);

        try {
            const response = await axios.post('https://api.finaloffers.fyi/jobOffers', { 
                ...jobOfferData, 
                offers: preparedOffers,
                additionalContent: content,  // Add the content for the entire document here
                submissionTimestamp: new Date() // Add the submission timestamp here 
            });
            console.log("Submit response:", response.data);
            navigate('/thank-you'); // Redirect to the thank you page on success
            // await axios.post('https://api.finaloffers.fyi/jobOffers', { ...jobOfferData, offers: preparedOffers });
        } catch (error) {
            console.error('There was an error submitting the form:', error);
        }
    };


    const addJobOffer = () => {
        setJobOfferData({
            ...jobOfferData,
            offers: [...jobOfferData.offers, {
                id: Date.now(),
                status: 'Choose Status',
                company: '',
                title: '',
                customTitle: '',
                jobFamily: '',
                focus: '',
                otherFocus: '',
                level: '',
                otherLevel: '',
                location: '',
                workMode: '',
                baseSalary: 0,
                baseSalaryCurrency: 'USD', 
                stock: 0,
                stockType: '',
                stockCurrency: 'USD', 
                signingBonus: 0,
                signingBonusCurrency: 'USD', 
                targetBonus: 0,
                targetBonusCurrency: 'USD', 
                totalCompensation: 0,
                month: months[currentMonth],
                year: currentYear,
                offerDate: '',
                searchResults: [],
                locationSearchResults: [],
                titleSearchResults: [],
                isDropdownVisible: false,
                isLocationDropdownVisible: false,
                isTitleDropdownVisible: false,
                jobFamilyDropdown: false,
                focusDropdown: false,
                levelDropdown: false, 
                isStatusDropdownVisible: false,
                isWorkModeDropdownVisible: false,
                isMonthDropdownVisible: false,
                isYearDropdownVisible: false
            }]
        });
    };

    const toggleDegreeDropdown = () => {
        setShowDegreeDropdown(!showDegreeDropdown);
    };


    const deleteJobOffer = (id) => {
        setJobOfferData({
            ...jobOfferData,
            offers: jobOfferData.offers.filter(offer => offer.id !== id)
        });
    };

    const transitions = useTransition(jobOfferData.offers, {
        keys: offer => offer.id,
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: config.gentle,
    });

    function selectCompany(offerId, company) {
        console.log(`Selected company: ${company} for offerId: ${offerId}`);
        const updatedOffers = jobOfferData.offers.map(o => {
            if (o.id === offerId) {
                return { ...o, company: company, isDropdownVisible: false };
            }
            return o;
        });
        setJobOfferData(prevData => ({
            ...prevData,
            offers: updatedOffers
        }));
    }

    const convertToUSD = (amount, currency) => {
        const rate = rates[currency];
        return (amount / rate).toFixed(2);
    };

    const handleStockTypeChange = (index, value, field) => {
        const updatedOffers = jobOfferData.offers.map((offer, idx) => {
            if (idx === index) {
                // return { ...offer, [field]: value };
                const updatedOffer = {
                    ...offer,
                    [field]: value
                };
                // Calculate Total Compensation when any related field changes
                // if (field === 'baseSalary' || field === 'stock' || field === 'targetBonus') {
                //     const totalCompensation = parseFloat(convertToUSD(updatedOffer.baseSalary, updatedOffer.baseSalaryCurrency) || 0)  + 
                //                                 parseFloat(convertToUSD(updatedOffer.stock, updatedOffer.stockCurrency) || 0) + 
                //                                 parseFloat(convertToUSD(updatedOffer.targetBonus, updatedOffer.targetBonusCurrency) || 0);
                //     updatedOffer.totalCompensation = totalCompensation;
                // }
                return updatedOffer;
            }
            return offer;
        });
        setJobOfferData(prevData => ({ ...prevData, offers: updatedOffers }));
    };

    const handleCurrencyChange = (index, value, field) => {
        const updatedOffers = jobOfferData.offers.map((offer, idx) => {
            if (idx === index) {
                // return { ...offer, [field]: value };
                const updatedOffer = {
                    ...offer,
                    [field]: value
                };
                // Calculate Total Compensation when any related field changes
                if (field === 'baseSalaryCurrency' || field === 'stockCurrency' || field === 'targetBonusCurrency') {
                    const totalCompensation = parseFloat(convertToUSD(updatedOffer.baseSalary, updatedOffer.baseSalaryCurrency) || 0)  + 
                                                parseFloat(convertToUSD(updatedOffer.stock, updatedOffer.stockCurrency) || 0) + 
                                                parseFloat(convertToUSD(updatedOffer.targetBonus, updatedOffer.targetBonusCurrency) || 0);
                    updatedOffer.totalCompensation = totalCompensation;
                }
                return updatedOffer;
            }
            return offer;
        });
        setJobOfferData(prevData => ({ ...prevData, offers: updatedOffers }));
    };

    const handleSalaryChange = (index, event, field) => {
        const value = event.target.value;
        const updatedOffers = jobOfferData.offers.map((offer, idx) => {
            if (idx === index) {
                // return { ...offer, [field]: value };
                const updatedOffer = {
                    ...offer,
                    [field]: value
                };
                // Calculate Total Compensation when any related field changes
                if (field === 'baseSalary' || field === 'stock' || field === 'targetBonus') {
                    const totalCompensation = parseFloat(convertToUSD(updatedOffer.baseSalary, updatedOffer.baseSalaryCurrency) || 0)  + 
                                                parseFloat(convertToUSD(updatedOffer.stock, updatedOffer.stockCurrency) || 0) + 
                                                parseFloat(convertToUSD(updatedOffer.targetBonus, updatedOffer.targetBonusCurrency) || 0);
                    updatedOffer.totalCompensation = totalCompensation;
                }
                return updatedOffer;
            }
            return offer;
        });
        setJobOfferData(prevData => ({ ...prevData, offers: updatedOffers }));
    };

    return (
        <div className="relative"> {/* Add relative here */}
            {showNotification && (
                <div className="absolute right-0 top-0 mt-4 mr-4 p-3 bg-black border rounded-md text-white shadow-lg z-50"> {/* Ensure it stays on top */}
                    <p className='text-lg'>Brother please. </p>
                    <button onClick={() => setShowNotification(false)} className="absolute top-0 right-0 bg-transparent bg-opacity-70 rounded-tr text-white p-1 mr-1">
                        <i class="fa-regular fa-circle-xmark"></i>
                    </button>
                    {notificationMessages.map((msg, index) => (
                        <p key={index} className='text-lg'>{index+1}. &nbsp;{msg}</p>
                    ))}
                </div>
            )}
            <div className=' mx-auto max-w-screen-md'>
            <form className="p-8 border-none" onSubmit={submitForm}>
                <h1 class="mb-4 text-6xl font-bold border-b border-black">Submit Your offers!</h1> 
                    <div class="grid sm:grid-cols-4 mb-4 sm:gap-6">
                        
                        {/* {!auth.isLoggedIn &&  ( */}
                        <div className="sm:col-span-4 relative">
                            {/* <div className="ml-1 mb-1  inline-flex text-center justify-between">
                                <div>
                                Email
                                </div>
                                
                                <div className='text-sm'>
                                    See why it is recommended? <i class="fa-solid fa-circle-info"></i>
                                </div>
                            </div>     */}
                            <div className="ml-1 mb-1 flex justify-between items-center">
                                <div className="text-left">
                                    Email
                                </div>
                                
                                <div className="text-sm text-right relative group">
                                    Highly recommended. See why? 
                                    <i className="fa-solid fa-circle-info cursor-pointer ml-1"></i>

                                    {/* Dialog Box (Tooltip) */}
                                    <div className="absolute left-0 mt-2 hidden group-hover:block bg-black border text-white p-2 shadow-lg rounded-md text-xs z-50 overflow-visible">
                                    Your email will be 100% confidential and will not be shown to anyone. It is only meant to help you access this submission if you choose to login or signup using this email in the future, and view reactions to it. 
                                    </div>
                                </div>

                                {/* <div className="text-sm text-right relative inline-flex">
                                    Highly recommended. See why? 
                                    <div className="group">
                                        <i className="fa-solid fa-circle-info cursor-pointer ml-1"></i>
                                        <div className="absolute left-0 mt-2 hidden group-hover:block bg-black border text-white p-2 shadow-lg rounded-md text-xs z-50 overflow-visible">
                                        Your email will be 100% confidential and will not be shown to anyone. It is only meant to help you access this submission if you choose to login or signup using this email in the future, and view reactions to it. 
                                        </div>
                                    </div>
                                    
                                </div> */}
                            </div>

                            <div className={`flex items-center border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] ${touched.email && !validateEmail(jobOfferData.email) ? 'border-red-500' : ''}`}>
                                {/* <i className="fa fa-envelope text-gray-400 text-lg ml-2"></i> */}
                                <MailIcon className="h-5 w-5 bg-white ml-3" />
                                <input
                                
                                    // name="email"
                                    type="email"
                                    className={`flex-1 text-sm/3 border-none rounded-r focus:ring-0 focus:border-none`}
                                    placeholder="johndoe@gmail.com"
                                    value={jobOfferData.email}
                                    onChange={(e) => setJobOfferData({ ...jobOfferData, email: e.target.value })}
                                    // onBlur={() => handleBlur('email')}
                                    // required
                                />
                            </div>
                            {/* <p className='text-xs mt-1'></p> */}
                        </div>
                        {/* )}  */}

                        <div class="sm:col-span-2">
                            <div className="text-sm ml-1 mb-1">Experience (In Years)</div>
                            <div className='relative'>
                                <div className='flex items-center border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                <div className='ml-3'>
                                    <i class="fa-solid fa-hourglass-half"></i>
                                </div>
                                <input
                                    type="number"
                                    name="experience"
                                    className={` ${touched.experience && !isExperienceValid ? 'border-red-500' : 'border-gray-300'} flex-1 text-sm/3 border-none rounded-r focus:ring-0 focus:border-none`}
                                    placeholder="4"
                                    value={jobOfferData.experience}
                                    onChange={(e) => setJobOfferData({ ...jobOfferData, experience: e.target.value })}
                                    // onBlur={() => handleBlur('experience')}
                                    // required
                                />
                                
                                </div>
                            </div>
                            {touched.experience && !isExperienceValid && (
                                <p className="text-red-500 text-xs mt-1">Please enter a valid number of years (0-50).</p>
                            )}
                        </div>

                        <div class="sm:col-span-2">
                            <div className="text-sm ml-1 mb-1">Highest Degree Obtained</div>
                            <div className="relative">
                                <button
                                    onClick={toggleDegreeDropdown}
                                    // onBlur={() => handleBlur('degree')}
                                    type="button"
                                    className='flex items-center border border-black rounded bg-white w-full h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '
                                >
                                    <AcademicCapIcon className="h-5 w-5 bg-white ml-3 mr-3"  /> 
                                    <div className='text-sm'>
                                        {jobOfferData.degree || 
                                            <div className='text-gray-500'> 
                                                PhD.
                                            </div>
                                        }
                                    </div>
                                    
                                    {jobOfferData.degree === '' && (<div className="pointer-events-none absolute right-0 flex items-center px-2">
                                        <ChevronDownIcon className="h-4 w-4" />
                                    </div>)}
                                </button>
                                {showDegreeDropdown && (
                                    <div className="absolute w-full top-full mt-1 text-sm z-50">
                                        <div className="bg-white border border-black rounded shadow-solid">
                                            {degreeOptions.map((status, idx) => (
                                                <div
                                                    key={idx}
                                                    onClick={() => {
                                                        // handleDetailChange(index, { target: { name: 'workMode', value: status } });
                                                        setJobOfferData({ ...jobOfferData, degree: status})
                                                        setShowDegreeDropdown(false);
                                                    }}
                                                    className={`px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer ${status === "" ? 'h-8' : ''}`}
                                                >
                                                    {status}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {touched.degree && !isDegreeSelected && (
                                <p className="text-red-500 text-xs mt-1">Please select the highest degree achieved.</p>
                            )}
                            </div>
                            
                        </div>
                    </div>

                {transitions((styles, offer, _, index) => (
                    <animated.div style={styles} key={offer.id} className="relative rounded p-10 my-6 border border-2 border-black">
                        
                        
                        <h3 className="flex justify-between items-center mb-2 pb-2 text-xl w-full border-dashed border-b-2 border-stone-300">
                            <span className="flex-1 flex text-2xl">
                                📝 Offer {jobOfferData.offers.length > 1 && (<>&nbsp;{index + 1}</>)} 
                                <div className='ml-2'>|</div> 
                                <div className='ml-4 relative w-32'>
                                    <button 
                                        onClick={() => toggleStatusDropdown(offer.id)} 
                                        type="button"
                                        className='flex items-center w-full h-9'
                                    >   
                                        {/* <BriefcaseIcon className="h-5 w-5 bg-white ml-3 mr-3"  />  */}
                                        <div className='text-sm'>
                                            {offer.status || ""}
                                        </div>
                                        
                                        {/* {offer.status === '' && (<div className="pointer-events-none absolute right-0 flex items-center px-2"> */}
                                            <ChevronDownIcon className="h-4 w-4 ml-2" />
                                        {/* </div>)} */}
                                    </button>
                                    {offer.isStatusDropdownVisible && (
                                        <div className="absolute top-full mt-1 w-full z-50">
                                            <div className="bg-white border border-black rounded text-sm shadow-solid">
                                                {statusOptions.map((status, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleStatusChange(offer.id, status)} 
                                                        className="px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer"
                                                    >
                                                        {status}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {index == 0 && (
                                    <div className="text-sm text-right relative group">
                                        <i className="fa-solid fa-circle-info cursor-pointer mt-[10px]"></i>

                                        {/* Dialog Box (Tooltip) */}
                                        <div className="absolute mt-2 hidden group-hover:block bg-black border text-white p-2 shadow-lg rounded-md text-xs z-50 text-left overflow-visible w-80">
                                        <div className='underline'>Denied:</div> Choosing this option means you were at least in the interview loop but could not clear all the interview rounds and therefore did not receive the final offer. <br/><br/>
                                        <div className='underline'>Offered:</div> Choosing this option means that you successfully cleared all the interview rounds and were able to receive the final offer. <br/><br/>
                                        <div className='underline'>Accepted:</div> Choosing this option means that you chose to accept this offer. 
                                        </div>
                                    </div>
                                )} 
                                {/* <div className="flex items-center bg-transparent">
                                <select
                                    value={offer.status}
                                    onChange={(e) => handleStatusChange(index, e)}
                                    style={{
                                        appearance: 'none',
                                        WebkitAppearance: 'none', // Note: React inline styles require the vendor prefix in camelCase
                                        MozAppearance: 'none',
                                        backgroundImage: 'none',
                                        backgroundColor: 'none',
                                        color: 'none',
                                        paddingRight: 0
                                    }}
                                    className="flex-1 text-sm/3 ml-2 pt-1 border-black border-b-2 border-t-0 border-l-0 border-r-0 pb-1 focus:ring-0 focus:border-none bg-none "
                                >
                                    <option value="" disabled>Choose Status</option>
                                    <option value="Denied">Denied</option>
                                    <option value="Offered">Offered</option>
                                    <option value="Accepted">Accepted</option>
                                    {/* </div> */}
                                {/* </select>
                                </div> */} 
                                {/* </button> */}
                            </span>
                            {/* Offer {index + 1} */}
                            {jobOfferData.offers.length > 1 && (
                                <button type="button" onClick={() => deleteJobOffer(offer.id)}>
                                    <TrashIcon className="size-7" />
                                </button>
                            )} 
                            
                        </h3>

                        <div class="grid gap-4 sm:grid-cols-4 mb-2 sm:gap-6">
                            <div class="sm:col-span-4">
                                <div className='text-sm ml-1 mb-1'>Company</div>
                                <div className="relative" >
                                    <div className='flex items-center border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                        <OfficeBuildingIcon className="h-5 w-5 bg-white ml-3" />
                                        <input 
                                        type="text" 
                                        class="flex-1 text-sm/3 border-none rounded-r focus:ring-0 focus:border-none" 
                                        
                                        required=""
                                        value={offer.company}
                                        onChange={(e) => handleCompanySearchChange(offer.id, e.target.value)}
                                        placeholder="FinalOffers"
                                        />
                                    </div>
                                    <div ref={wrapperRef}>
                                    {offer.isDropdownVisible && (
                                        <div className="absolute border border-black w-80 bg-white text-sm rounded mt-2 shadow-solid ml-8 z-20">
                                        {offer.searchResults.map((result, idx) => (
                                            <div
                                                key={idx}
                                                className="p-2 w-full cursor-pointer hover:bg-gray-100 flex items-center z-50"
                                                onClick={() => selectCompany(offer.id, result.name)}
                                            >
                                                <img src={`https://logo.clearbit.com/${result.domain}`} alt={`${result.name} logo`} className="h-5 w-5 mr-2" />
                                                {result.name}
                                            </div>
                                        ))}
                                        </div>
                                    )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="grid sm:grid-cols-2 mb-2 mt-4 gap-x-6 gap-y-4">
                            <div class="sm:col-span-2">
                                <div className='text-sm ml-1 mb-1'>Title</div>
                                <div className="relative" >
                                    <div className='flex items-center border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                        <UserCircleIcon className="h-5 w-5 bg-white ml-3" />
                                        <input 
                                        type="text" 
                                        class="flex-1 text-sm/3 border-none rounded-r focus:ring-0 focus:border-none" 
                                        
                                        required=""
                                        value={offer.title}
                                        onChange={(e) => handleTitleSearchChange(offer.id, e.target.value)}
                                        placeholder="Software Engineer"
                                        />
                                    </div>
                                    {/* {offer.isTitleDropdownVisible && offer.titleSearchResults.length > 0 && (
                                        <div className="absolute border border-black w-80 bg-white text-sm rounded mt-2 shadow-solid ml-8 z-20">
                                        {offer.titleSearchResults.map((result, idx) => (
                                            <div
                                                key={idx}
                                                className="pinline-flex w-full cursor-pointer "
                                                onClick={() => {
                                                    const updatedOffers = jobOfferData.offers.map(o => {
                                                        console.log("Selected title:", result.title);
                                                        if (o.id === offer.id) {
                                                            console.log(o);
                                                            return { ...o, title: result.title, 
                                                                    jobFamily: result.family,
                                                                    isTitleDropdownVisible: false };
                                                        }
                                                        return o;
                                                    });
                                                    setJobOfferData({ ...jobOfferData, offers: updatedOffers });
                                                }}
                                            >
                                                <div className='inline-flex w-full pl-3 pt-1 pb-1 hover:bg-black hover:text-white '>
                                                    {result.title}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    )}
                                    {offer.isTitleDropdownVisible && offer.titleSearchResults.length < 5 && (
                                        <div className="absolute border border-black w-80 bg-white text-sm rounded mt-2 shadow-solid ml-8 z-20">
                                            <div
                                                className="pinline-flex w-full cursor-pointer "
                                                onClick={() => {
                                                    // Update the company name of the offer
                                                    const updatedOffers = jobOfferData.offers.map(o => {
                                                        console.log("Selected title:", "Other");
                                                        if (o.id === offer.id) {
                                                            console.log(o);
                                                            return { ...o, title: "Other", isTitleDropdownVisible: false };
                                                        }
                                                        return o;
                                                    });
                                                    setJobOfferData({ ...jobOfferData, offers: updatedOffers });
                                                }}
                                            >
                                                <div className='inline-flex w-full pl-3 pt-1 pb-1 hover:bg-black hover:text-white '>
                                                    Not Present Above? Click to add your own
                                                </div>
                                            </div>
                                        </div>
                                    )} */}
                                    {offer.isTitleDropdownVisible && (
                                        <div className="absolute border border-black w-80 bg-white text-sm rounded mt-2 shadow-solid ml-8 z-20">
                                            {offer.titleSearchResults.map((result, idx) => (
                                                <div
                                                    key={idx}
                                                    className="pinline-flex w-full cursor-pointer"
                                                    onClick={() => {
                                                        const updatedOffers = jobOfferData.offers.map(o => {
                                                            if (o.id === offer.id) {
                                                                return { ...o, 
                                                                    title: result.title, 
                                                                    jobFamily: result.family, 
                                                                    focus: result.focus,
                                                                    level: result.level,
                                                                    isTitleDropdownVisible: false 
                                                                };
                                                            }
                                                            return o;
                                                        });
                                                        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
                                                    }}
                                                >
                                                    <div className='inline-flex w-full pl-3 pt-1 pb-1 hover:bg-black hover:text-white'>
                                                        {result.title}
                                                    </div>
                                                </div>
                                            ))}
                                            <div
                                                className="pinline-flex w-full cursor-pointer"
                                                onClick={() => {
                                                    const updatedOffers = jobOfferData.offers.map(o => {
                                                        if (o.id === offer.id) {
                                                            return { ...o, title: "Other", isTitleDropdownVisible: false };
                                                        }
                                                        return o;
                                                    });
                                                    setJobOfferData({ ...jobOfferData, offers: updatedOffers });
                                                }}
                                            >
                                                <div className='inline-flex w-full pl-3 pt-1 pb-1 hover:bg-black hover:text-white'>
                                                    Not Present in the Dropdown? Click to add your own
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {offer.title != "" && offer.title != "Other" && (<p className="text-xs mt-1">
                                    Job Family: {offer.jobFamily}
                                </p>)}
                            </div>
                            {offer.title === "Other"  && (
                                <div className='col-span-1'>
                                    <div className='text-sm ml-1 mb-1'>Custom Title</div>
                                    <div className='flex items-center border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                        <input 
                                        type="text" 
                                        class="flex-1 text-sm/3 border-none rounded focus:ring-0 focus:border-none" 
                                        
                                        required=""
                                        value={offer.customTitle}
                                        // onChange={(e) => handleCompanySearchChange(offer.id, e.target.value)}
                                        onChange={(e) => handleCustomTitleChange(offer.id, e.target.value)}
                                        placeholder="Co-CEO"
                                        />
                                    </div>
                                </div>
                            
                            )}
                            {offer.title === "Other"  && (
                                <div className='col-span-1'>
                                    <div className='relative'>
                                        <div className='text-sm ml-1 mb-1'>Any guess for Job Family? It's optional BTW</div>
                                        <button 
                                            onClick={() => toggleJobFamilyDropdown(offer.id)} 
                                            type="button"
                                            className='flex items-center border w-full border-black rounded bg-white h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px]'
                                        >   
                                            <div className='mx-3'>
                                                <i class="fa-solid fa-users"></i>
                                            </div>
                                            
                                            <div className='text-sm'>
                                                {offer.jobFamily || ""}
                                            </div>
                                            
                                            <div className="pointer-events-none absolute right-0 flex items-center px-2">
                                                <ChevronDownIcon className="h-4 w-4" />
                                            </div>
                                        </button>
                                        {offer.jobFamilyDropdown && (
                                            <div className="absolute top-full mt-1 w-full z-50">
                                                <div className="bg-white border border-black rounded text-sm shadow-solid">
                                                    {jobFamilyOptions.map((jobFamily, idx) => (
                                                        <div
                                                            key={idx}
                                                            onClick={() => handleJobFamilyChange(offer.id, jobFamily)} 
                                                            className={`px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer ${jobFamily === "" ? 'h-8' : ''}`}
                                                        >
                                                            {jobFamily}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div class="sm:col-span-1">
                                <div class="relative">
                                    <div className='text-sm ml-1 mb-1'>Focus/Sub-Domain</div>
                                    <button 
                                        onClick={() => toggleFocusDropdown(offer.id)} 
                                        type="button"
                                        className='flex items-center border w-full border-black rounded bg-white h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px]'
                                    >   
                                        <div className='mx-3'>
                                            <i class="fa-solid fa-magnifying-glass"></i>
                                        </div>
                                        
                                        <div className='text-sm'>
                                            {offer.focus || ""}
                                        </div>
                                        
                                        <div className="pointer-events-none absolute right-0 flex items-center px-2">
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </div>
                                    </button>
                                    {offer.focusDropdown && (
                                        <div className="absolute top-full mt-1 w-full z-50">
                                            <div className="bg-white border border-black rounded text-sm shadow-solid">
                                                {focusOptions.map((focus, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleFocusChange(offer.id, focus)} 
                                                        className={`px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer ${focus === "" ? 'h-8' : ''}`}
                                                    >
                                                        {focus}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {offer.focus === "Other" && (
                                        <div className='flex items-center mt-3 border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                            <input 
                                            type="text" 
                                            class="flex-1 text-sm/3 border-none rounded focus:ring-0 focus:border-none" 
                                            
                                            required=""
                                            value={offer.otherFocus}
                                            // onChange={(e) => handleCompanySearchChange(offer.id, e.target.value)}
                                            onChange={(e) => handleOtherFocusChange(offer.id, e.target.value)}
                                            placeholder="Technical Writing"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div class="sm:col-span-1">
                                <div class="relative">
                                    <div className='text-sm ml-1 mb-1'>Level</div>
                                    <button 
                                        onClick={() => toggleLevelDropdown(offer.id)} 
                                        type="button"
                                        className='flex items-center border w-full border-black rounded bg-white h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px]'
                                    >   
                                        <div className='mx-3'>
                                            <i class="fa-solid fa-stairs"></i>
                                        </div>
                                        
                                        <div className='text-sm'>
                                            {offer.level || ""}
                                        </div>
                                        
                                        <div className="pointer-events-none absolute right-0 flex items-center px-2">
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </div>
                                    </button>
                                    {offer.levelDropdown && (
                                        <div className="absolute top-full mt-1 w-full z-50">
                                            <div className="bg-white border border-black rounded text-sm shadow-solid">
                                                {levelOptions.map((level, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleLevelChange(offer.id, level)} 
                                                        className={`px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer ${level === "" ? 'h-8' : ''}`}
                                                    >
                                                        {level}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {offer.level === "Other" && (
                                        <div className='flex items-center mt-3 border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                        <input 
                                        type="text" 
                                        class="flex-1 text-sm/3 border-none rounded focus:ring-0 focus:border-none" 
                                        
                                        required=""
                                        value={offer.otherLevel}
                                        // onChange={(e) => handleCompanySearchChange(offer.id, e.target.value)}
                                        onChange={(e) => handleOtherLevelChange(offer.id, e.target.value)}
                                        placeholder="Boss"
                                        />
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-4 mt-6 sm:gap-6">
                            <div className="sm:col-span-3">
                                <div className='text-sm ml-1 mb-1'>Location</div>
                                <div className='flex items-center border border-black rounded bg-white focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                    <LocationMarkerIcon className="h-5 w-5 bg-white ml-3" />
                                    <input 
                                    type="text" 
                                    class="flex-1 text-sm/3 border-none rounded-r focus:ring-0 focus:border-none" 
                                    
                                    required=""
                                    value={offer.location}
                                    onChange={(e) => handleLocationSearchChange(offer.id, e.target.value)}
                                    placeholder="New York, NY, USA"
                                    />
                                </div>
                                <div className="relative" ref={locationWrapperRef}>
                                    {offer.isLocationDropdownVisible && (
                                        <div className="absolute border border-black w-80 bg-white text-sm rounded mt-2 shadow-solid ml-8 z-20">
                                            {offer.locationSearchResults.map((location, idx) => (
                                                <div
                                                    key={idx}
                                                    className="pinline-flex w-full cursor-pointer "
                                                    onClick={() => {
                                                        // Update the company name of the offer
                                                        const updatedOffers = jobOfferData.offers.map(o => {
                                                            console.log("Selected company:", location);
                                                            if (o.id === offer.id) {
                                                                console.log(o);
                                                                return { ...o, location: location, isLocationDropdownVisible: false };
                                                            }
                                                            return o;
                                                        });
                                                        setJobOfferData({ ...jobOfferData, offers: updatedOffers });
                                                    }}
                                                >   
                                                    <div className='inline-flex w-full pl-3 pt-1 pb-1 hover:bg-black hover:text-white '>
                                                    {location}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="sm:col-span-1">
                                <div className='text-sm ml-1 mb-1'>Work Mode</div>
                                <div className='relative'>
                                    <button 
                                        onClick={() => toggleWorkModeDropdown(offer.id)} 
                                        type="button"
                                        className='flex items-center border w-full border-black rounded bg-white h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px]'
                                    >   
                                        <BriefcaseIcon className="h-5 w-5 bg-white ml-3 mr-3"  /> 
                                        <div className='text-sm'>
                                            {offer.workMode || ""}
                                        </div>
                                        
                                        {offer.workMode === '' && (<div className="pointer-events-none absolute right-0 flex items-center px-2">
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </div>)}
                                    </button>
                                    {offer.isWorkModeDropdownVisible && (
                                        <div className="absolute top-full mt-1 w-full z-50">
                                            <div className="bg-white border border-black rounded text-sm shadow-solid">
                                                {workModeOptions.map((workMode, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleWorkModeChange(offer.id, workMode)} 
                                                        className="px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer"
                                                    >
                                                        {workMode}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {(offer.status === 'Accepted' || offer.status === 'Offered') && (
                        <>
                            <div className="mt-10 mb-6">
                                <div className="grid grid-cols-2 gap-4 mb-2 mt-4">
                                    <div className="sm:col-span-1 ">
                                        <div className='text-sm ml-1 mb-[6px]'>
                                            Annual Base Salary
                                        </div>
                                        <div className='flex h-8'>
                                            <select value={offer.baseSalaryCurrency} onChange={(e) => handleCurrencyChange(index, e.target.value, 'baseSalaryCurrency')}
                                                className='bg-black text-white rounded-l pl-5 mr-0 appearance-none border-black focus:ring-0 focus:border-black cursor-pointer'
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none'}}
                                            >
                                                {Object.keys(rates).map((key) => (
                                                    <option key={key} value={key}>{key}</option>
                                                ))}
                                            </select> 
                                            <input
                                                type="number"
                                                className='text-sm w-full rounded-r h-8 focus:ring-0 border-black focus:border-black'
                                                value={offer.baseSalary}
                                                onChange={(e) => handleSalaryChange(index, e, 'baseSalary')}
                                                placeholder="100,000"
                                            />
                                        </div>
                                        {(offer.baseSalaryCurrency != "USD") && (<p className='text-xs'>Converted to USD: ${convertToUSD(offer.baseSalary, offer.baseSalaryCurrency).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>)}
                                    </div>
                                    <div className="sm:col-span-1">
                                        <div className='text-sm ml-1 mb-1 flex justify-between items-center'>
                                            Average Yearly Stock
                                            <select 
                                                value={offer.stockType} 
                                                onChange={(e) => handleStockTypeChange(index, e.target.value, 'stockType')}
                                                className='bg-black text-white text-sm rounded pl-3 mr-0 appearance-none border-black focus:ring-0 focus:border-black cursor-pointer'
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none'}}
                                            >
                                                <option value="">Choose Type</option>
                                                <option value="Options">Options</option>
                                                <option value="RSUs">RSUs</option>
                                            </select>
                                        </div>
                                        <div className='flex h-8 rounded focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                            <select 
                                                value={offer.stockCurrency} 
                                                onChange={(e) => handleCurrencyChange(index, e.target.value, 'stockCurrency')}
                                                className='bg-black text-white rounded-l pl-5 mr-0 appearance-none border-black focus:ring-0 focus:border-black cursor-pointer'
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none'}}
                                            >
                                                {Object.keys(rates).map((key) => (
                                                    <option key={key} value={key}>{key}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                className='text-sm w-full rounded-r h-8 focus:ring-0 border-black focus:border-black'
                                                value={offer.stock}
                                                onChange={(e) => handleSalaryChange(index, e, 'stock')}
                                                placeholder="72,003"
                                            />
                                            
                                        </div>
                                        {(offer.stockCurrency != "USD") && (<p className='text-xs'>Converted to USD: ${convertToUSD(offer.stock, offer.stockCurrency).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>)}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-2 pb-4 border-black border-b-2 mt-4">
                                    <div className="sm:col-span-1">
                                        <div className='text-sm ml-1 mb-1'>
                                            Yearly Target Bonus
                                        </div>
                                        <div className='flex h-8 rounded focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                            <select 
                                                value={offer.targetBonusCurrency} 
                                                onChange={(e) => handleCurrencyChange(index, e.target.value, 'targetBonusCurrency')}
                                                className='bg-black text-white rounded-l pl-5 mr-0 appearance-none border-black focus:ring-0 focus:border-black cursor-pointer'
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none'}}
                                            >
                                                {Object.keys(rates).map((key) => (
                                                    <option key={key} value={key}>{key}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                className='text-sm w-full rounded-r h-8 focus:ring-0 border-black focus:border-black'
                                                value={offer.targetBonus}
                                                onChange={(e) => handleSalaryChange(index, e, 'targetBonus')}
                                                placeholder="21,700"
                                            />
                                        </div>
                                        {(offer.targetBonusCurrency != "USD") && (<p className='text-xs'>Converted to USD: ${convertToUSD(offer.targetBonus, offer.targetBonusCurrency).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p>)}
                                    </div>
                                    <div className="sm:col-span-1">
                                        <div className='text-sm ml-1 mb-1'>
                                            Total Sign-On Bonus
                                        </div>
                                        <div className='flex h-8 rounded focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px] '>
                                            <select 
                                                value={offer.signingBonusCurrency} 
                                                onChange={(e) => handleCurrencyChange(index, e.target.value, 'signingBonusCurrency')}
                                                className='bg-black text-white rounded-l pl-5 mr-0 appearance-none border-black focus:ring-0 focus:border-black cursor-pointer'
                                                style={{ WebkitAppearance: 'none', MozAppearance: 'none'}}
                                            >
                                                {Object.keys(rates).map((key) => (
                                                    <option key={key} value={key}>{key}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                className='text-sm w-full rounded-r h-8 focus:ring-0 border-black focus:border-black'
                                                value={offer.signingBonus}
                                                onChange={(e) => handleSalaryChange(index, e, 'signingBonus')}
                                                placeholder="21,700"
                                            />
                                        </div>
                                        {(offer.signingBonusCurrency != "USD") && (<p className='text-xs'>Converted to USD: ${convertToUSD(offer.signingBonus, offer.signingBonusCurrency).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</p> )}                                      
                                    </div>
                                </div>
                                <div>
                                    <div className='text-lg font-bold text-center'>
                                        Total Compensation: {jobOfferData.offers[index].totalCompensation?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || "0"}
                                    </div>
                                </div>
                            </div>
                            <div className="grid sm:grid-cols-4 mb-2 mt-4 sm:gap-6"> 
                            <div className='sm:col-span-2'>
                                <div className='text-sm ml-1 mb-1'>&nbsp;</div>
                                <div className='py-1 font-bold'>
                                    When did you received your offer?
                                </div>
                            </div>
                            <div className='sm:col-span-1'>
                                <div className='text-sm ml-1 mb-1'>Month</div>
                                <div className='relative'>
                                    <button 
                                        onClick={() => toggleMonthDropdown(index)} 
                                        type="button"
                                        className='flex items-center border w-full border-black rounded bg-white h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px]'
                                    >   
                                        <div className='text-sm ml-3'>
                                            {offer.month || ""}
                                        </div>
                                        
                                        <div className="pointer-events-none absolute right-0 flex items-center px-2">
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </div>
                                    </button>
                                    {offer.isMonthDropdownVisible && (
                                        <div className="absolute top-full mt-1 w-full z-50">
                                            <div className="bg-white border border-black rounded shadow-solid overflow-y-scroll max-h-96">
                                                {months.map((month, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleOfferDateMonthChange(offer.id, month)} 
                                                        className="px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer"
                                                    >
                                                        {month}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='sm:col-span-1'>
                                <div className='text-sm ml-1 mb-1'>Year</div>
                                <div className='relative'>
                                    <button 
                                        onClick={() => toggleYearDropdown(index)} 
                                        type="button"
                                        className='flex items-center border w-full border-black rounded bg-white h-9 focus-within:ring-[#cfcfb4] focus-within:ring-offset focus-within:ring-[3px]'
                                    >   
                                        {/* <BriefcaseIcon className="h-5 w-5 bg-white ml-3 mr-3"  />  */}
                                        <div className='text-sm ml-3'>
                                            {offer.year || ""}
                                        </div>
                                        
                                        <div className="pointer-events-none absolute right-0 flex items-center px-2">
                                            <ChevronDownIcon className="h-4 w-4" />
                                        </div>
                                    </button>
                                    {offer.isYearDropdownVisible && (
                                        <div className="absolute top-full mt-1 w-full z-50">
                                            <div className="bg-white border border-black rounded shadow-solid overflow-y-scroll max-h-96">
                                                {years.map((year, idx) => (
                                                    <div
                                                        key={idx}
                                                        onClick={() => handleOfferDateYearChange(offer.id, year)} 
                                                        className="px-4 py-2 hover:bg-black hover:text-white hover:rounded cursor-pointer"
                                                    >
                                                        {year}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                        </div>
                        </>
                        )}
                        
                    </animated.div>
                ))}
                <div className="sm:col-span-2">
                    <label htmlFor="description" className="block mb-2 font-medium text-gray-900 dark:text-white">
                        Care to share more about your experience, preparation etc.?
                    </label>
                    <TextEditor initialValue={content} onChange={handleEditorChange} />
                    {/* <TextEditor /> */}
                </div>

                <div className="flex justify-between mt-4">
                    <button type="button" onClick={addJobOffer} className="border border-black rounded hover:bg-black hover:text-white px-5 py-2.5">
                        Add Offer
                    </button>
                    <button type="submit" className="text-white bg-black font-medium rounded px-5 py-2.5">
                        Submit
                    </button>
                </div>
            </form>
        </div>
        </div>
    );
}


export default Form;
